import type { KeyValueStorage } from './storage.js';

const PREFIX = 'chat:rascunho:';

export type Draft = {
  content: string;
  /** Respondendo a esta mensagem. */
  replyTo?: { messageId: string; mention: boolean } | null;
  updatedAt: number;
};

/**
 * Rascunho por canal, como no Discord: o texto fica se a pessoa troca de
 * canal, fecha o app ou cai a conexão. Em memória para ler na hora; gravado
 * no armazenamento local para sobreviver ao fechar.
 */
export class DraftStore {
  private readonly cache = new Map<string, Draft>();

  constructor(
    private readonly storage: KeyValueStorage,
    /** Separa os rascunhos de cada conta no mesmo aparelho. */
    private readonly scope: string = 'padrao',
  ) {}

  private key(channelId: string) {
    return `${PREFIX}${this.scope}:${channelId}`;
  }

  /** O que está em memória (lido antes por `load`). */
  get(channelId: string): Draft | null {
    return this.cache.get(channelId) ?? null;
  }

  async load(channelId: string): Promise<Draft | null> {
    if (this.cache.has(channelId)) return this.cache.get(channelId) ?? null;
    const raw = await this.storage.getItem(this.key(channelId));
    if (!raw) return null;
    try {
      const draft = JSON.parse(raw) as Draft;
      this.cache.set(channelId, draft);
      return draft;
    } catch {
      return null;
    }
  }

  /** Grava (vazio apaga). A interface chama com espera (debounce) ao digitar. */
  async set(
    channelId: string,
    content: string,
    replyTo: Draft['replyTo'] = null,
    now: number = Date.now(),
  ): Promise<void> {
    if (!content.trim() && !replyTo) {
      await this.clear(channelId);
      return;
    }
    const draft: Draft = { content, replyTo, updatedAt: now };
    this.cache.set(channelId, draft);
    await this.storage.setItem(this.key(channelId), JSON.stringify(draft));
  }

  async clear(channelId: string): Promise<void> {
    this.cache.delete(channelId);
    await this.storage.removeItem(this.key(channelId));
  }
}
